import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const mockJobs = [
  { id: 'JOB001', type: 'food', emoji: '🍔', title: 'Food Delivery', hotel: 'Country Plaza', room: '101', detail: 'Burger, Flat White x2', pay: 8, distance: '1st floor', status: 'available' },
  { id: 'JOB002', type: 'driver', emoji: '🚗', title: 'Driver Request', hotel: 'Kookaburra', room: '205', detail: 'Hotel to Halls Gap CBD', pay: 25, distance: '12 min drive', status: 'available' },
  { id: 'JOB003', type: 'photo', emoji: '📸', title: 'Photo Session', hotel: 'Mountain View', room: '312', detail: 'Standard Session, Garden', pay: 35, distance: 'Garden area', status: 'available' },
];

const accents = {
  food: '#FFFFFF',
  driver: '#5AC8FA',
  photo: '#BF9BE8',
};

function StaffDashboard() {
  const navigate = useNavigate();
  const staffName = localStorage.getItem('staffName') || 'Staff';
  const [jobs, setJobs] = useState(mockJobs);
  const [filter, setFilter] = useState('all');

  const acceptJob = (id) => setJobs(jobs.map(j => j.id === id ? { ...j, status: 'accepted' } : j));
  const completeJob = (id) => setJobs(jobs.map(j => j.id === id ? { ...j, status: 'completed' } : j));

  const handleLogout = () => {
    localStorage.removeItem('staffAuth');
    localStorage.removeItem('staffName');
    navigate('/staff-login');
  };

  const earnings = jobs.filter(j => j.status === 'completed').reduce((s, j) => s + j.pay, 0);
  const potential = jobs.filter(j => j.status !== 'completed').reduce((s, j) => s + j.pay, 0);
  const completedCount = jobs.filter(j => j.status === 'completed').length;

  const filtered = filter === 'all' ? jobs : jobs.filter(j => j.type === filter);
  const firstName = staffName.split(' ')[0];

  return (
    <div style={styles.page}>
      {/* Soft ambient light */}
      <div style={styles.ambient} />

      {/* Top bar */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={styles.topBar}
      >
        <div style={styles.avatar}>{firstName.charAt(0).toUpperCase()}</div>
        <div style={styles.topText}>
          <p style={styles.greeting}>Good to see you</p>
          <h1 style={styles.name}>{firstName}</h1>
        </div>
        <motion.button whileTap={{ scale: 0.94 }} style={styles.logoutBtn} onClick={handleLogout}>
          Sign out
        </motion.button>
      </motion.div>

      {/* Earnings — the hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        style={styles.heroCard}
      >
        <div style={styles.heroGlow} />
        <p style={styles.heroLabel}>Earned today</p>
        <div style={styles.heroAmount}>
          <span style={styles.heroCurrency}>$</span>
          <span style={styles.heroNumber}>{earnings}</span>
        </div>
        <div style={styles.heroMeta}>
          <span style={styles.heroMetaItem}>
            <span style={styles.heroMetaNum}>{completedCount}</span> jobs done
          </span>
          <span style={styles.heroMetaDot}>·</span>
          <span style={styles.heroMetaItem}>
            <span style={styles.heroMetaNum}>${potential}</span> still available
          </span>
        </div>
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          style={{ ...styles.cashoutBtn, opacity: earnings > 0 ? 1 : 0.4, cursor: earnings > 0 ? 'pointer' : 'not-allowed' }}
        >
          Cash out ${earnings}
        </motion.button>
      </motion.div>

      {/* Segmented filter */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        style={styles.segment}
      >
        {[
          { id: 'all', label: 'All' },
          { id: 'food', label: 'Food' },
          { id: 'driver', label: 'Rides' },
          { id: 'photo', label: 'Photo' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            style={styles.segmentBtn}
          >
            {filter === tab.id && (
              <motion.div layoutId="segmentPill" style={styles.segmentPill} transition={{ type: 'spring', stiffness: 400, damping: 32 }} />
            )}
            <span style={{ ...styles.segmentText, color: filter === tab.id ? '#0a0a0a' : '#6b6b6b' }}>
              {tab.label}
            </span>
          </button>
        ))}
      </motion.div>

      {/* Jobs */}
      <div style={styles.list}>
        <AnimatePresence mode="popLayout">
          {filtered.map((job, i) => (
            <motion.div
              layout
              key={job.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              style={styles.card}
            >
              <div style={styles.cardTop}>
                <div style={{ ...styles.iconChip, background: accents[job.type] + '1a' }}>
                  <span style={styles.chipEmoji}>{job.emoji}</span>
                </div>
                <div style={styles.cardInfo}>
                  <p style={styles.cardTitle}>{job.title}</p>
                  <p style={styles.cardSub}>{job.hotel} · {job.distance}</p>
                </div>
                <div style={styles.payTag}>
                  <span style={styles.payPlus}>+</span>
                  <span style={{ ...styles.payNum, color: accents[job.type] }}>${job.pay}</span>
                </div>
              </div>

              <div style={styles.cardDetail}>
                <span style={styles.roomChip}>Room {job.room}</span>
                <span style={styles.detailText}>{job.detail}</span>
              </div>

              {job.status === 'available' && (
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  style={{ ...styles.primaryBtn, background: accents[job.type] }}
                  onClick={() => acceptJob(job.id)}
                >
                  Accept
                </motion.button>
              )}
              {job.status === 'accepted' && (
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={styles.completeBtn}
                  onClick={() => completeJob(job.id)}
                >
                  Mark complete
                </motion.button>
              )}
              {job.status === 'completed' && (
                <div style={styles.doneRow}>
                  <span style={styles.doneCheck}>✓</span>
                  <span style={styles.doneText}>Completed · ${job.pay} added</span>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <p style={styles.footer}>POWERED BY <span style={styles.gold}>REDSPEED</span></p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0a',
    maxWidth: '430px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif',
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: '40px',
  },
  ambient: {
    position: 'absolute', top: '-150px', left: '50%', transform: 'translateX(-50%)',
    width: '500px', height: '400px',
    background: 'radial-gradient(ellipse, rgba(232,185,106,0.08) 0%, transparent 65%)',
    pointerEvents: 'none',
  },
  topBar: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '52px 20px 24px',
    position: 'relative', zIndex: 1,
  },
  avatar: {
    width: '44px', height: '44px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #FFFFFF, #CCCCCC)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', fontWeight: '700', color: '#0a0a0a',
    flexShrink: 0,
  },
  topText: { flex: 1 },
  greeting: { fontSize: '13px', color: '#6b6b6b', margin: '0 0 1px', fontWeight: '400' },
  name: { fontSize: '22px', fontWeight: '600', color: '#fff', margin: 0, letterSpacing: '-0.4px' },
  logoutBtn: {
    background: 'transparent', border: 'none',
    color: '#6b6b6b', fontSize: '14px', cursor: 'pointer',
    fontFamily: 'inherit', padding: '8px',
  },
  heroCard: {
    margin: '0 16px 24px',
    background: 'linear-gradient(150deg, #19191c 0%, #121214 100%)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '28px', padding: '28px 24px 22px',
    position: 'relative', zIndex: 1, overflow: 'hidden',
  },
  heroGlow: {
    position: 'absolute', top: '-60px', right: '-40px',
    width: '180px', height: '180px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(232,185,106,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroLabel: { fontSize: '14px', color: '#8a8a8a', margin: '0 0 6px', fontWeight: '400' },
  heroAmount: { display: 'flex', alignItems: 'flex-start', gap: '2px', marginBottom: '14px' },
  heroCurrency: { fontSize: '28px', fontWeight: '600', color: '#FFFFFF', marginTop: '6px' },
  heroNumber: { fontSize: '64px', fontWeight: '700', color: '#fff', lineHeight: 1, letterSpacing: '-2px' },
  heroMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' },
  heroMetaItem: { fontSize: '13px', color: '#8a8a8a' },
  heroMetaNum: { color: '#fff', fontWeight: '600' },
  heroMetaDot: { color: '#444' },
  cashoutBtn: {
    width: '100%', background: '#fff', color: '#0a0a0a',
    border: 'none', borderRadius: '16px', padding: '15px',
    fontSize: '16px', fontWeight: '600', fontFamily: 'inherit',
    letterSpacing: '-0.2px',
  },
  segment: {
    display: 'flex', gap: '0',
    margin: '0 16px 20px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '14px', padding: '4px',
    position: 'relative', zIndex: 1,
  },
  segmentBtn: {
    flex: 1, position: 'relative',
    background: 'transparent', border: 'none',
    padding: '9px 0', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  segmentPill: {
    position: 'absolute', inset: 0,
    background: '#fff', borderRadius: '10px',
  },
  segmentText: {
    position: 'relative', zIndex: 1,
    fontSize: '14px', fontWeight: '600',
    transition: 'color 0.2s ease',
  },
  list: {
    padding: '0 16px',
    display: 'flex', flexDirection: 'column', gap: '12px',
    position: 'relative', zIndex: 1,
  },
  card: {
    background: '#151517',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: '22px', padding: '18px',
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '14px' },
  iconChip: {
    width: '50px', height: '50px', borderRadius: '15px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  chipEmoji: { fontSize: '24px' },
  cardInfo: { flex: 1 },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 2px', letterSpacing: '-0.2px' },
  cardSub: { fontSize: '13px', color: '#6b6b6b', margin: 0 },
  payTag: { display: 'flex', alignItems: 'flex-start', gap: '1px' },
  payPlus: { fontSize: '14px', color: '#6b6b6b', marginTop: '3px' },
  payNum: { fontSize: '22px', fontWeight: '700', letterSpacing: '-0.5px' },
  cardDetail: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: 'rgba(255,255,255,0.03)', borderRadius: '12px',
    padding: '11px 13px', marginBottom: '14px',
  },
  roomChip: {
    fontSize: '11px', fontWeight: '700', color: '#fff',
    background: 'rgba(255,255,255,0.08)', borderRadius: '7px',
    padding: '4px 9px', flexShrink: 0,
  },
  detailText: { fontSize: '13px', color: '#9a9a9a' },
  primaryBtn: {
    width: '100%', color: '#0a0a0a', border: 'none',
    borderRadius: '14px', padding: '14px',
    fontSize: '15px', fontWeight: '600', fontFamily: 'inherit',
    cursor: 'pointer', letterSpacing: '-0.2px',
  },
  completeBtn: {
    width: '100%', background: '#34C759', color: '#fff',
    border: 'none', borderRadius: '14px', padding: '14px',
    fontSize: '15px', fontWeight: '600', fontFamily: 'inherit',
    cursor: 'pointer', letterSpacing: '-0.2px',
  },
  doneRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', padding: '13px',
    background: 'rgba(52,199,89,0.1)', borderRadius: '14px',
  },
  doneCheck: {
    width: '18px', height: '18px', borderRadius: '50%',
    background: '#34C759', color: '#fff', fontSize: '11px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: '700',
  },
  doneText: { fontSize: '14px', fontWeight: '500', color: '#34C759' },
  footer: {
    textAlign: 'center', fontSize: '9px', letterSpacing: '3px',
    color: '#333', marginTop: '32px',
    position: 'relative', zIndex: 1,
  },
  gold: { color: '#FFFFFF' },
};

export default StaffDashboard;